import React from 'react';
import { string, object, bool } from 'prop-types';
import classNames from 'classnames';

import { getCountryFromAddress } from './Agreement.helpers';
import { stringifyDateToISO, timestampToDate } from '../../util/dates';

import css from './Agreement.module.css';

const Agreement = props => {
  const { className, rootClassName, currentListing, currentUser } = props;
  const classes = classNames(rootClassName || css.root, className);

  // 1. Centralized User Data Extraction
  const { attributes: { profile, email } = {} } = currentUser || {};
  const { firstName = '', lastName = '', publicData: { businessName, businessNumber } = {} } =
    profile || {};
  const fullName = `${firstName} ${lastName}`.trim();

  // 2. Listing Data with Default Values
  const { publicData = {} } = currentListing?.attributes || {};
  const { newAgreementSignedDate, location = {} } = publicData;
  const { building, address } = location;

  // 3. Derived Formatting Logic
  const fullAddress = [building, address].filter(Boolean).join(', ');
  const country = getCountryFromAddress(address);

  // 4. Date Logic: Fallback to current time if signed date is missing
  const timestamp = newAgreementSignedDate || Date.now();
  const insertDate = stringifyDateToISO(timestampToDate(timestamp));
  return (
    <div className={classes}>
      <h2 className={css.title}>Independent Contractor Agreement</h2>
      <p>
        <strong>This Agreement</strong> is made on {insertDate}.
      </p>
      <p>
        Between: <strong>Bendelta Pty Ltd</strong> (ACN 105 151 326), Level 5, 32–36 York Street,
        Sydney NSW 2000, Australia (“Bendelta”, “we”, or “us”), and
        <strong> The Independent Contractor </strong>({fullName}, {email}, {fullAddress}
        {businessName ? `, ${businessName}` : ''}
        {businessNumber ? `, ACN ${businessNumber}` : ''}) ("you" or "Contractor").
      </p>
      <p>
        Our intention is to work together in a way that is professional, respectful, simple and
        human. The following terms help us both understand expectations clearly and support a smooth
        collaboration, wherever in the world you may be working from.
      </p>
      <h3>1. Your Role as an Independent Contractor</h3>
      <p>
        1.1. We value your expertise and trust you to deliver your services in the way that works
        best for you and the client.
      </p>
      <p>
        1.2. You are engaged as an independent contractor, not an employee or agent of Bendelta.
        This means:
      </p>
      <ul>
        <li>you choose how, when and where to carry out your work;</li>
        <li>you are free to work with other clients or on other platforms; and</li>
        <li>
          you are responsible for taxes, insurances, business operating costs and any legal
          requirements in the country you live or work in.
        </li>
      </ul>
      <p>
        1.3. You agree to handle all tax and legal obligations applicable to you, including social
        security, pension (superannuation), GST/VAT (if relevant), and other local legal
        requirements.
      </p>
      <h3>2. What You Will Do (Your Services)</h3>
      <p>
        2.1. You will provide Professional Coaching Services, which may include 1:1 coaching, team
        or group coaching, and assessment tool debriefs (the “Services”).
      </p>
      <p>
        2.2. If you identify further sales opportunities after a Bendelta client has been introduced
        to you (either through your own initiative or a client requests directly with you), this
        work must be performed on behalf of Bendelta as per this Agreement. For the avoidance of
        doubt, these Services must not be carried out directly by you, independent of Bendelta.
      </p>
      <p>
        2.3. If we agree on additional work other than professional coaching, such as facilitation
        or consulting ("Additional Services"), we’ll document this in a separate Scope of Work (the
        “SOW”). This helps keep things clear and avoids misunderstandings.
      </p>
      <p>
        2.4. Each SOW will outline things like deliverables, timelines, additional terms, fees, and
        what success looks like and will be an annexure to this Agreement. To the extent there is
        any inconsistency between the terms of this Agreement and the SOW, the terms of the SOW will
        apply.
      </p>
      <p>
        2.5. In the course of delivering the Services and any Additional Services, you agree to
        comply with all Bendelta policies and procedures that have been made available to you (but
        these policies and procedures do not form part of this Agreement).
      </p>
      <h3>3. Fees and Payments</h3>
      <p>
        3.1. For providing the Services, we’ll pay you 90% of the rate you propose and we accept for
        each engagement you are booked to deliver.
      </p>
      <p>
        3.2. The rate you propose for each engagement should be inclusive of all tax and business
        costs you incur to deliver the Services. In other words, the rate you propose should
        represent your gross hourly income in USD.
      </p>
      <p>
        3.3. We may apply an additional charge to the client, with reference to the cost of
        delivery. This charge does not form part of what we will pay you.
      </p>
      <p>3.4. Additional Services under a SOW will be paid as described in that SOW.</p>
      <p>
        3.5. You are responsible for sending invoices to Bendelta that follow the tax rules of your
        country.
      </p>
      <p>
        3.6. We will pay you within 21 days of receiving your invoice after the work is completed
        and invoiced to the client.
      </p>
      <p>3.7. You agree to issue a maximum of one invoice to Bendelta per month.</p>
      <p>3.8. Payments are normally made in USD, unless we agree otherwise.</p>
      <h3>4. Taxes (Including GST, VAT and International Taxes)</h3>
      <p>
        4.1. You’re responsible for knowing and meeting your tax obligations in all relevant
        jurisdictions (both within Australia and internationally), including registration, reporting
        and payment obligations relating to GST, VAT, withholding tax or similar taxes which may
        apply to the Services. This may be impacted by where you are located and / or where the
        client is located. As we are not authorised to provide tax advice, we recommend you seek
        specialist advice from a registered tax agent if you have any doubts on the appropriate tax
        treatment of the Services.
      </p>
      <p>
        4.2. Bendelta isn’t responsible for any penalties, interest charges or issues that arise
        from you not fully meeting your tax obligations.
      </p>
      <p>
        4.3. The invoices you provide to us must comply with the taxation legislation. If you fail
        to provide us with a tax invoice when requested or required to do so, we may withhold some
        or all of our payment to you until you comply.
      </p>
      <h3>5. Insurance</h3>
      <p>
        5.1. To protect both you and us, you are required to maintain the following during your
        engagement:
        <ul>
          <li>Workers’ Compensation or similar insurance, if required where you live.</li>
        </ul>
      </p>
      <p>
        5.2. You may be located anywhere in the world, so this insurance must cover you for the
        places where you physically perform the Services and any Additional Services.
      </p>
      <p>
        5.3. We also recommended that you hold Public Liability and Professional Indemnity
        Insurance. Should a specific engagement, covered under a SOW, require different or
        additional insurances, these would be detailed separately in the SOW.
      </p>
      <h3>6. Subcontractors and Others Helping You</h3>
      <p>
        6.1. If you’d like to involve anyone else in delivering the Services or any Additional
        Services, such as a personal assistant, please check with us first.
      </p>
      <p>
        6.2. Anyone helping you must follow the same confidentiality, privacy and IP protections
        that you agree to in this Agreement.
      </p>
      <p>
        6.3. You remain responsible for their work and ensuring all contractual conditions are met.
      </p>
      <h3>7. Confidentiality</h3>
      <p>
        7.1. During the course of providing the Services and any Additional Services, you’ll likely
        have access to Confidential Information which is of a confidential nature and of value to
        Bendelta. To protect the confidentiality of the Confidential Information, you must at all
        times both during and after providing the Services and any Additional Services (unless
        Bendelta expressly authorises in writing otherwise):
      </p>
      <p>
        7.1.1. keep all Confidential Information, whether oral, graphic, electronic, written or in
        any other form, which you receive, possess or acquire confidential and take all reasonable
        steps to keep secure and not disclose the Confidential Information to any other person;
      </p>
      <p>
        7.1.2. not make any copy of any Confidential Information, unless it is in the proper
        performance of your duties in providing the Services and any Additional Services under this
        Agreement, in which case the copy or reproduction will belong to Bendelta;
      </p>
      <p>
        7.1.3. not use, or attempt to use, Confidential Information in a way which may cause loss to
        Bendelta; and
      </p>
      <p>
        7.1.4. otherwise use Confidential Information solely in accordance with this Agreement and
        any SOW.
      </p>
      <p>
        7.2. You will also likely learn things in your coaching conversations that relate to the
        coachee and their organisation. You must always ensure that this information remains
        confidential and is not shared with any other person, unless otherwise required by law.
      </p>
      <p>7.3. If you encounter a breach of confidentiality, you must let us know immediately.</p>
      <p>
        7.4. If this Agreement ends, you must return or securely delete all Confidential Information
        and materials that belong to Bendelta or our coachees and clients.
      </p>
      <p>7.5. These obligations continue even after our relationship ends.</p>
      <p>
        7.6. "Confidential Information" is confidential information of Bendelta and its related
        bodies corporate including all trade secrets and all financial, accounting, marketing and
        technical information, customer and supplier lists (including any prospective or proposed
        customer and supplier lists) or information, authorised agent's information, ideas,
        concepts, know-how, intellectual property, results of experiments and tests, technology,
        operating procedures, processes, business methods, knowledge and other information whether
        or not such information is reduced to a tangible form or marked in writing as
        "confidential", but excluding information in the public domain otherwise than because of a
        breach of confidentiality owed by any person.
      </p>
      <h3>8. Privacy and Handling Personal Data</h3>
      <p>
        8.1. Because our work often involves sensitive conversations and personal information, we
        both need to handle data with care.
      </p>
      <p>8.2. You agree to: </p>
      <ul>
        <li>follow applicable privacy laws in the jurisdiction in which you work. </li>
        <li>store information securely, using encrypted systems or methods where possible; and </li>
        <li>tell us immediately if any data may have been lost or accessed without permission</li>
        <li>not use unauthorised AI or recording devices as part of your coaching engagement.</li>
      </ul>
      <h3>9. Intellectual Property</h3>
      <p>9.1. Any materials or content you created before working with us remain yours.</p>
      <p>
        9.2. Any provision of Bendelta IP (materials, content, tools etc) to assist you in your
        coaching engagement, remains the sole property of Bendelta and must not be used or modified
        for any other purpose.
      </p>
      <h3>10. Doing the Work Well (Warranties)</h3>
      <p>10.1. You confirm that you will:</p>
      <ul>
        <li>
          only perform work in line with your capabilities, qualifications, and accreditations;
        </li>
        <li>
          perform the Services with care and professionalism, free from risk to the health and
          safety of yourself, our clients and coachees, or any employee of Bendelta;{' '}
        </li>
        <li>
          let us know if any conflicts of interest appear, for either Bendelta, a coachee or a
          client and
        </li>
        <li>act in a way that supports the reputation of Bendelta and our coachees and clients.</li>
      </ul>
      <h3>11. Indemnity</h3>
      <p>11.1. You agree to indemnify us against losses that arise because of:</p>
      <ul>
        <li>your breach of this Agreement (including any SOW);</li>
        <li>negligence or misconduct;</li>
        <li>issues with intellectual property in materials you provide;</li>
        <li>data breaches caused by your systems or personnel; or</li>
        <li>tax, employment or regulatory claims in your jurisdiction.</li>
        <li>This clause simply ensures we’re protected from risks we can’t control.</li>
      </ul>
      <h3>12. Ending This Agreement</h3>
      <p>
        12.1. Either of us may end this Agreement for any reason by giving 4 weeks’ written notice.
      </p>
      <p>
        12.2. We may end the Agreement immediately if you engage in serious misconduct or criminal
        behaviour, breach confidentiality, fail to maintain required insurance, or engage in
        behaviour that could significantly harm Bendelta’s reputation.
      </p>
      <p>
        12.3. When the Agreement ends, please provide us with any work in progress. You must also
        return or delete any confidential information.
      </p>
      <p>
        12.4. For Additional Services, other than Professional Coaching, the SOW will detail the
        specific end date for that discrete engagement. This agreement will continue irrespective of
        the end date of a SOW, unless terminated as per 12.1 or 12.2.
      </p>
      <h3>13. Execution</h3>
      <p>This document may be executed electronically using the BOLDLY Marketplace platform.</p>
      <h3>14. Governing Law</h3>
      <p>
        14.1. This Agreement is governed by the laws of New South Wales, Australia, regardless of
        where you live or work.
      </p>
      <p>
        14.2. Any dispute arising from this Agreement will be arbitrated according to the laws of
        New South Wales, Australia.
      </p>
      <p>14.3. You are responsible for following any laws in your own country that apply to you.</p>
      <h3>15. Entire Agreement</h3>
      <p>
        15.1. This document contains everything we’ve agreed to. Any changes will need to be in
        writing and signed by both of us.
      </p>
      <div className={css.infoGroup}>
        <div className={css.info}>
          <h3>Schedule 1 - Your details</h3>
          <p>Name: {fullName}</p>
          <p>Email Address: {email}</p>
          <p>Business Name (if applicable): {businessName}</p>
          <p>Business Registration Number (if applicable): {businessNumber}</p>
          <p>Country of Residence: {country}</p>
        </div>
        <div className={css.info}>
          <h3>Acknowledging the Agreement</h3>
          <p>We're looking forward to working together.</p>
        </div>
        <div className={css.info}>
          <h3>Acknowledged by Bendelta Pty Ltd: </h3>
          <p>Name: Natalie Micarone</p>
          <p>Date: {insertDate}</p>
        </div>
        <div className={css.info}>
          <h3>Acknowledged by the Contractor:</h3>
          <p>Name: {fullName}</p>
          <p>Date: {insertDate}</p>
        </div>
      </div>
    </div>
  );
};

Agreement.defaultProps = {
  className: null,
  rootClassName: null,
  currentListing: null,
  currentUser: null,
};

Agreement.propTypes = {
  className: string,
  rootClassName: string,
  currentListing: object,
  currentUser: object,
};

export default Agreement;
